import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  X,
  Bot,
  Search,
  Sparkles,
  PhoneCall,
  Headphones,
  CreditCard,
  ShieldCheck,
  Calendar,
  Landmark,
  Check
} from "lucide-react";
import Button from "../Button/Button.js";
import SearchBar from "../SearchBar/SearchBar.js";
import Tabs from "../Tabs/Tabs.js";
import Modal from "../Modal/Modal.js";
import "./TemplateCatalogModal.css";

const AGENT_TEMPLATES = [
  {
    id: "outbound_sales",
    title: "Outbound Lead Gen & Sales",
    category: "Sales & Outbound",
    language: "Hinglish",
    gender: "female",
    voice_character_id: "char-5",
    icon: <PhoneCall size={22} style={{ color: "#2563eb" }} />,
    iconBg: "#eff6ff",
    badgeColor: "#dbeafe",
    badgeTextColor: "#1d4ed8",
    description: "High-energy sales assistant designed to qualify leads, pitch key features, and handle pricing objections.",
    initial_message: "Hello! Mera naam Aditi hai ValueEnable se. Kya aap automated IVR sales setup ke baare me discuss karna chahenge?",
    prompt_sections: [
      {
        id: "welcome_message",
        title: "Welcome Message / Initial Greeting",
        prompt: "Hello! Mera naam Aditi hai ValueEnable se. Kya aap automated IVR sales setup ke baare me discuss karna chahenge?",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "conversation_flow",
        title: "Conversation Flow",
        prompt: "You are Aditi, an energetic lead generation executive at ValueEnable.\n- Pitch key benefits of AI voice automation.\n- Qualify the lead by asking about call volume and current setup.\n- Offer a quick live demo with our product manager.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "special_cases",
        title: "Special Cases",
        prompt: "If the caller says they are busy or driving, offer to schedule a quick callback or send a WhatsApp summary brochure.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "rebuttals_faqs",
        title: "Rebuttals / FAQs",
        prompt: "Q: What is the pricing?\nA: Our plans start at $0.10 per minute with zero setup cost.\nQ: Does it support Hindi?\nA: Yes, we support Hinglish, Hindi, English, and 12 regional Indian languages.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "final_call_message",
        title: "Final Call Message",
        prompt: "Thank you for your time! I will send the demo details on your WhatsApp. Have a great day ahead!",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      }
    ]
  },
  {
    id: "customer_support",
    title: "Tier-1 Customer Support",
    category: "Support & Inbound",
    language: "English",
    gender: "female",
    voice_character_id: "char-1",
    icon: <Headphones size={22} style={{ color: "#0284c7" }} />,
    iconBg: "#e0f2fe",
    badgeColor: "#e0f2fe",
    badgeTextColor: "#0369a1",
    description: "Empathetic, polite support representative for resolving account queries, troubleshooting, and handling complaints.",
    initial_message: "Hi, thanks for calling ValueEnable Support. How can I assist you with your account today?",
    prompt_sections: [
      {
        id: "welcome_message",
        title: "Welcome Message / Initial Greeting",
        prompt: "Hi, thanks for calling ValueEnable Support. How can I assist you with your account today?",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "conversation_flow",
        title: "Conversation Flow",
        prompt: "You are a customer support specialist.\n- Listen attentively to the customer's issue.\n- Ask for their policy number or registered mobile number.\n- Provide step-by-step guidance to resolve the issue.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "special_cases",
        title: "Special Cases",
        prompt: "If the customer is angry or upset, apologize politely and assure them of prompt resolution. Escalate to a human supervisor if requested.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "rebuttals_faqs",
        title: "Rebuttals / FAQs",
        prompt: "Q: How do I reset my password?\nA: You can click on 'Forgot Password' on the login page or I can trigger a reset link to your email.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "final_call_message",
        title: "Final Call Message",
        prompt: "Thank you for contacting ValueEnable Support. Have a wonderful rest of your day!",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      }
    ]
  },
  {
    id: "payment_recovery",
    title: "Payment & Premium Recovery",
    category: "Collections & BFSI",
    language: "Hinglish",
    gender: "female",
    voice_character_id: "char-3",
    icon: <CreditCard size={22} style={{ color: "#ca8a04" }} />,
    iconBg: "#fef9c3",
    badgeColor: "#fef9c3",
    badgeTextColor: "#854d0e",
    description: "Assertive, professional agent for notifying pending premiums/EMIs, explaining grace periods, and sending instant payment links.",
    initial_message: "Hello {customer_name}! Main ValueEnable se bol rahi hu regarding your pending policy premium of {pending_premium}.",
    prompt_sections: [
      {
        id: "welcome_message",
        title: "Welcome Message / Initial Greeting",
        prompt: "Hello {customer_name}! Main ValueEnable se bol rahi hu regarding your pending policy premium of {pending_premium}.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "conversation_flow",
        title: "Conversation Flow",
        prompt: "You are a collections executive calling about policy premium renewal.\n- Remind {customer_name} that policy {policy_number} premium of {pending_premium} is due on {payment_due_date}.\n- Explain benefits of timely renewal to avoid policy lapse.\n- Offer to send an instant payment link on SMS.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "special_cases",
        title: "Special Cases",
        prompt: "If caller asks for extension, offer a 3-day grace period notice.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "rebuttals_faqs",
        title: "Rebuttals / FAQs",
        prompt: "Q: Can I pay online?\nA: Yes, I can send a secure UPI/card payment link directly on your WhatsApp now.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "final_call_message",
        title: "Final Call Message",
        prompt: "Thank you! The payment link has been sent to your phone. Have a good day!",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      }
    ]
  },
  {
    id: "policy_service",
    title: "Insurance Policy Desk",
    category: "Insurance",
    language: "Hinglish",
    gender: "female",
    voice_character_id: "char-2",
    icon: <ShieldCheck size={22} style={{ color: "#16a34a" }} />,
    iconBg: "#dcfce7",
    badgeColor: "#dcfce7",
    badgeTextColor: "#15803d",
    description: "Specialized desk for checking fund values, policy renewal status, nominee updates, and claim filings.",
    initial_message: "Namaste {customer_name}! Welcome to ValueEnable Insurance Desk. How can I help with your policy {policy_number}?",
    prompt_sections: [
      {
        id: "welcome_message",
        title: "Welcome Message / Initial Greeting",
        prompt: "Namaste {customer_name}! Welcome to ValueEnable Insurance Desk. How can I help with your policy {policy_number}?",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "conversation_flow",
        title: "Conversation Flow",
        prompt: "You are an insurance advisor.\n- Verify customer identity.\n- Answer questions regarding fund value {fund_value}, maturity amount, or nominee {nominee_name}.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "special_cases",
        title: "Special Cases",
        prompt: "If claim filing is requested, guide customer to upload documents on our portal.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "rebuttals_faqs",
        title: "Rebuttals / FAQs",
        prompt: "Q: What is my current fund value?\nA: Your current fund value is {fund_value}.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "final_call_message",
        title: "Final Call Message",
        prompt: "Thank you for reaching out to ValueEnable Insurance. Stay safe!",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      }
    ]
  },
  {
    id: "appointment_booking",
    title: "Appointment & Callback Scheduler",
    category: "Scheduling",
    language: "English",
    gender: "female",
    voice_character_id: "char-4",
    icon: <Calendar size={22} style={{ color: "#9333ea" }} />,
    iconBg: "#f3e8ff",
    badgeColor: "#f3e8ff",
    badgeTextColor: "#6b21a8",
    description: "Automated receptionist for booking consultation slots, confirming times, and sending SMS confirmation details.",
    initial_message: "Hello! Welcome to ValueEnable Scheduling Services. Would you like to book a consultation slot today?",
    prompt_sections: [
      {
        id: "welcome_message",
        title: "Welcome Message / Initial Greeting",
        prompt: "Hello! Welcome to ValueEnable Scheduling Services. Would you like to book a consultation slot today?",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "conversation_flow",
        title: "Conversation Flow",
        prompt: "You are a scheduling assistant.\n- Ask for preferred date and time slot.\n- Confirm contact details.\n- Send SMS confirmation.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "special_cases",
        title: "Special Cases",
        prompt: "If preferred slot is unavailable, offer alternative nearby slots.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "rebuttals_faqs",
        title: "Rebuttals / FAQs",
        prompt: "Q: What are your working hours?\nA: Our advisors are available Monday to Saturday from 9 AM to 7 PM.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "final_call_message",
        title: "Final Call Message",
        prompt: "Your appointment is confirmed! A calendar invite has been sent to your email.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      }
    ]
  },
  {
    id: "loan_desk",
    title: "Personal Loan & Banking Desk",
    category: "Collections & BFSI",
    language: "Hindi",
    gender: "female",
    voice_character_id: "char-6",
    icon: <Landmark size={22} style={{ color: "#ea580c" }} />,
    iconBg: "#ffedd5",
    badgeColor: "#ffedd5",
    badgeTextColor: "#c2410c",
    description: "Personal loan assistant for explaining interest rates, checking eligibility criteria, and guiding document submission.",
    initial_message: "Namaste {customer_name}! Main ValueEnable Banking desk se bol rahi hu. Kya aap personal loan offers check karna chahenge?",
    prompt_sections: [
      {
        id: "welcome_message",
        title: "Welcome Message / Initial Greeting",
        prompt: "Namaste {customer_name}! Main ValueEnable Banking desk se bol rahi hu. Kya aap personal loan offers check karna chahenge?",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "conversation_flow",
        title: "Conversation Flow",
        prompt: "You are a personal loan assistant.\n- Explain pre-approved loan offers.\n- Verify monthly salary and employment status.\n- Guide customer to submit KYC documents online.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "special_cases",
        title: "Special Cases",
        prompt: "If customer asks for lower interest rates, explain slab rates based on credit score.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "rebuttals_faqs",
        title: "Rebuttals / FAQs",
        prompt: "Q: What is the processing fee?\nA: Processing fee is 1% of the loan amount.",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      },
      {
        id: "final_call_message",
        title: "Final Call Message",
        prompt: "Aapke phone par loan link bhej diya gaya hai. Dhanyawad!",
        variables: { preprocessing_fields: {}, additional_fields: {} }
      }
    ]
  }
];

const CATEGORIES = ["All", "Sales & Outbound", "Support & Inbound", "Collections & BFSI", "Insurance", "Scheduling"];

export default function TemplateCatalogModal({ isOpen, onClose, onSelectTemplate }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredTemplates = AGENT_TEMPLATES.filter((tpl) => {
    const matchesCategory = selectedCategory === "All" || tpl.category === selectedCategory;
    const matchesSearch =
      tpl.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tpl.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tpl.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Start With Template"
      subtitle="Deploy preconfigured, industry-tested voice agent scripts with ready variables."
      icon={<Sparkles size={20} style={{ color: "#2563eb" }} />}
      maxWidth="860px"
    >
      <div className="template-modal-body-content">
        {/* Search & Category Filter Bar */}
        <div className="template-filter-bar">
          <SearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates by industry, topic, or keyword..."
            width="100%"
          />

          <div className="template-category-tabs">
            <Tabs
              tabs={[
                { id: "All", label: "All" },
                { id: "Sales & Outbound", label: "Sales & Outbound" },
                { id: "Support & Inbound", label: "Support & Inbound" },
                { id: "Collections & BFSI", label: "Collections & BFSI" },
                { id: "Insurance", label: "Insurance" },
                { id: "Scheduling", label: "Scheduling" }
              ]}
              activeTab={selectedCategory}
              onChange={(tabId) => setSelectedCategory(tabId)}
              variant="pills"
              color="primary"
            />
          </div>
        </div>

        {/* Templates Catalog Grid */}
        <div className="template-catalog-grid">
          {filteredTemplates.length === 0 ? (
            <div className="template-empty-state">
              <Bot size={32} style={{ color: "#94a3b8", marginBottom: 12 }} />
              <h4>No templates found matching "{searchQuery}"</h4>
              <p>Try searching with another keyword or select a different category filter.</p>
            </div>
          ) : (
            filteredTemplates.map((tpl) => (
              <div key={tpl.id} className="template-card">
                <div className="template-card-header">
                  <div className="template-card-icon" style={{ backgroundColor: tpl.iconBg }}>
                    {tpl.icon}
                  </div>
                  <div>
                    <h4 className="template-card-title">{tpl.title}</h4>
                    <div className="template-card-tags">
                      <span className="template-tag category" style={{ backgroundColor: tpl.badgeColor, color: tpl.badgeTextColor }}>
                        {tpl.category}
                      </span>
                      <span className="template-tag language">
                        {tpl.language}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="template-card-description">{tpl.description}</p>

                {/* Initial Message Preview Box */}
                <div className="template-preview-box">
                  <span className="preview-label">Opening Greeting:</span>
                  <p className="preview-text">"{tpl.initial_message}"</p>
                </div>

                <div className="template-card-footer">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onSelectTemplate(tpl)}
                    className="btn-use-template"
                    style={{ width: "100%", justifyContent: "center", display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 700 }}
                  >
                    <Sparkles size={14} />
                    <span>Use Template</span>
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

TemplateCatalogModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectTemplate: PropTypes.func.isRequired
};
